import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import CategoryCard from '@/components/base/CategoryCard';
import ArticleCard from '@/components/base/ArticleCard';
import FAQSection from '@/components/base/FAQSection';
import { categories, hotArticles, getCategoryBySlug } from '@/mocks/categories';
import { faqList } from '@/mocks/faq';

export default function Home() {
  const organizationLd = {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: '黄油游戏下载',
    url: 'https://www.huangyougame.com/',
    description: '最全面的黄油游戏下载教程、安全渠道推荐及常见问题解答平台',
    contactPoint: {
      '@type': 'ContactPoint',
      email: 'contact@huangyougame.com',
      contactType: 'customer service',
    },
  };

  const websiteLd = {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: '黄油游戏下载',
    url: 'https://www.huangyougame.com/',
    potentialAction: {
      '@type': 'SearchAction',
      target: 'https://www.huangyougame.com/search?q={search_term_string}',
      'query-input': 'required name=search_term_string',
    },
  };

  const webpageLd = {
    '@context': 'https://schema.org',
    '@type': 'WebPage',
    name: '黄油游戏下载 - 官方正版免费获取指南',
    description: '想要黄油游戏下载？本页提供最全面的黄油游戏下载教程、安全渠道推荐及常见问题解答，帮助玩家快速、安全地完成黄油游戏下载，畅享优质游戏体验。',
    url: 'https://www.huangyougame.com/',
    isPartOf: {
      '@type': 'WebSite',
      name: '黄油游戏下载',
    },
  };

  const breadcrumbLd = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      {
        '@type': 'ListItem',
        position: 1,
        name: '首页',
        item: 'https://www.huangyougame.com/',
      },
    ],
  };

  return (
    <>
      <ScrollToTop />
      <script type="application/ld+json">
        {JSON.stringify(organizationLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(webpageLd)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(breadcrumbLd)}
      </script>

      <Navbar />

      <main>
        <section className="relative w-full h-[550px] md:h-[650px] flex items-center justify-center overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Epic%20gaming%20illustration%20with%20vibrant%20warm%20orange%20and%20amber%20gradient%20background%2C%20stylized%20fantasy%20game%20characters%20silhouettes%2C%20floating%20magical%20particles%2C%20digital%20art%20style%2C%20cinematic%20composition%20with%20dramatic%20lighting%2C%20no%20text&width=1800&height=1200&seq=hero-home-2026&orientation=landscape"
            alt="黄油游戏下载 - 游戏下载教程平台"
            title="黄油游戏下载 - 游戏下载指南"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/30 to-black/50"></div>
          <div className="relative z-10 w-full text-center px-4 md:px-6">
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading text-white mb-4 leading-tight">
              黄油游戏下载
            </h1>
            <p className="text-base md:text-lg text-white/80 max-w-2xl mx-auto mb-8 leading-relaxed">
              最全面的黄油游戏下载教程平台，提供安全可靠的下载渠道推荐、
              详细的安装指南和常见问题解答，让每一位玩家都能快速安全地完成游戏下载
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/action"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap shadow-lg shadow-primary-500/25"
              >
                开始探索
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/20 text-white border border-white/30 rounded-full text-sm font-semibold hover:bg-background-50/30 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                了解更多
              </Link>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                游戏分类浏览
              </h2>
              <p className="text-sm text-foreground-600 max-w-xl mx-auto">
                按照游戏类型快速找到你感兴趣的内容，每个分类都有详细的下载教程和安装指南
              </p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {categories.map((cat) => (
                <CategoryCard
                  key={cat.slug}
                  slug={cat.slug}
                  name={cat.name}
                  description={cat.description}
                  icon={cat.icon}
                  imageSeq={cat.imageSeq}
                  color={cat.color}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
            <div className="flex flex-col sm:flex-row items-start sm:items-end justify-between mb-10 gap-4">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-2">
                  热门教程文章
                </h2>
                <p className="text-sm text-foreground-600">
                  精选最受玩家欢迎的游戏下载教程和安装指南
                </p>
              </div>
              <Link
                to="/category/action"
                className="flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
              >
                查看全部
                <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {hotArticles.map((article) => (
                <ArticleCard
                  key={article.slug}
                  slug={article.slug}
                  title={article.title}
                  excerpt={article.excerpt}
                  date={article.date}
                  readTime={article.readTime}
                  imageSeq={article.imageSeq}
                  isHot={article.isHot}
                />
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="w-full px-4 md:px-6 max-w-3xl mx-auto">
            <div className="text-center mb-10">
              <h2 className="text-2xl md:text-3xl font-heading text-foreground-900 mb-3">
                常见问题解答
              </h2>
              <p className="text-sm text-foreground-600">
                关于黄油游戏下载的常见疑问，这里都有答案
              </p>
            </div>
            <FAQSection items={faqList} />
          </div>
        </section>

        <section className="py-14 md:py-20 relative overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=Warm%20inviting%20gaming%20community%20illustration%20with%20players%20connecting%2C%20orange%20and%20amber%20gradient%20aesthetic%2C%20modern%20digital%20art%20style%2C%20cinematic%20composition%20with%20soft%20glowing%20particles&width=1800&height=800&seq=cta-home-2026&orientation=landscape"
            alt="加入黄油游戏下载社区"
            title="黄油游戏下载 - 社区"
            className="absolute inset-0 w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/35 to-black/50"></div>
          <div className="relative z-10 text-center px-4 md:px-6 w-full max-w-2xl mx-auto">
            <h2 className="text-2xl md:text-3xl font-heading text-white mb-3">
              准备好开始你的游戏之旅了吗？
            </h2>
            <p className="text-sm md:text-base text-white/70 mb-8">
              浏览我们的分类教程，找到最适合你的游戏下载指南
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link
                to="/category/action"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-all duration-300 cursor-pointer whitespace-nowrap"
              >
                浏览教程
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/20 text-white border border-white/30 rounded-full text-sm font-semibold hover:bg-background-50/30 transition-all duration-300 cursor-pointer whitespace-nowrap backdrop-blur-sm"
              >
                关于我们
              </Link>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}